import React from "react";
import { Box, Flex, type FlexProps, Icon } from "@chakra-ui/react";
import type { Theme } from "contexts/color-theme";
import { type BrandColorToken } from "theme/colors";

export function HeroSpotlightBackgroundEffects({
  theme,
  bgColor,
  spotlightColor,
  spotlightHeight = "100%",
  hasBgForHalfHero,
}: {
  theme: Theme;
  bgColor: BrandColorToken;
  spotlightColor: string; // Use a `BrandColorToken` most of the time, but a few exceptions exist.
  spotlightHeight?: FlexProps["height"];
  hasBgForHalfHero?: boolean;
}) {
  return (
    <>
      <Flex pos="absolute" left="0" right="0" bottom="0" height={spotlightHeight} justifyContent="center">
        {/** Mobile spotlight */}
        <Icon
          display={{ base: "inline-block", md: "none" }}
          w="768px"
          minH="1180px"
          h={{ base: "130%", lg: "1180px" }}
          viewBox="0 0 768 1180"
          fill="none"
        >
          <g filter="url(#hero-mobile-b)" clipPath="url(#hero-mobile-a)">
            <Box
              as="path"
              d="M1391 1421c0 556.15-450.849 1007-1007 1007-556.151 0-1007-450.85-1007-1007 0-556.151 450.849-1007 1007-1007 556.151 0 1007 450.849 1007 1007Z"
              fill={spotlightColor}
            />
          </g>
          <defs>
            <clipPath id="hero-mobile-a">
              <path fill="white" d="M0 0h768v1180H0z" />
            </clipPath>
            <filter
              id="hero-mobile-b"
              x="-1023"
              y="14"
              width="2814"
              height="1180"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_334_5098" />
            </filter>
          </defs>
        </Icon>

        {/** Desktop spotlight */}
        <Icon
          display={{ base: "none", md: "inline-block" }}
          width="2900px"
          height="1422px"
          viewBox="0 0 2900 1422"
          fill="none"
        >
          <g filter="url(#hero-desktop-b)" clipPath="url(#hero-desktop-a)">
            <Box as="ellipse" cx="1450" cy="1277" rx="1081" ry="855" fill={spotlightColor} />
          </g>
          <defs>
            <clipPath id="hero-desktop-a">
              <path fill="white" d="M0 0h2900v1422H0z" />
            </clipPath>
            <filter
              id="hero-desktop-b"
              x="0"
              y="0"
              width="2900"
              height="1422"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="200" result="effect1_foregroundBlur_334_3139" />
            </filter>
          </defs>
        </Icon>

        {hasBgForHalfHero && (
          /** Bg shown behind half hero media sections in horizontal layout on lg+ screens. */
          <Icon
            display={{ base: "none", lg: "inline-block" }}
            pos="absolute"
            width="2312"
            height="1125"
            viewBox="0 0 2312 1125"
            fill="none"
          >
            <g filter="url(#hero-color-b)" clipPath="url(#hero-color-a)">
              <Box as="circle" cx="1340" cy="65" r="676" fill={bgColor} />
            </g>
            <defs>
              <clipPath id="hero-color-a">
                <path fill="white" d="M0 0h2312v1125H0z" />
              </clipPath>
              <filter
                id="hero-color-b"
                x="364"
                y="-911"
                width="1952"
                height="1952"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                <feGaussianBlur stdDeviation="150" result="effect1_foregroundBlur_11570_7433" />
              </filter>
            </defs>
          </Icon>
        )}
      </Flex>

      {/** Top fade to bg color to ensure a smooth blend with the header. */}
      <Box bg={bgColor} pos="absolute" left="-40px" right="-40px" top="-80px" h="160px" filter="blur(50px)" />

      {/** Bottom extra-wide fade to white for light themes, which is a bit more appealing on very-wide screens. */}
      {theme === "light" && (
        <>
          <Box
            bg={spotlightColor}
            pos="absolute"
            left="-40px"
            right="-40px"
            bottom="-50px"
            h="140px"
            filter="blur(80px)"
          />
          <Box
            bg={spotlightColor}
            pos="absolute"
            left="-40px"
            right="-40px"
            bottom="-50px"
            h="100px"
            filter="blur(30px)"
          />
        </>
      )}
    </>
  );
}
